import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import OnboardingChecklist from '../components/OnboardingChecklist';
import TestoFormattato from '../components/TestoFormattato';
import { BadgeCopertura, descriviContratto } from '../components/CoperturaContratto';
import {
  Edit2, Save, X, Plus, CheckCircle2, Clock, Circle,
  Users, Compass, Settings2, Search, CheckSquare2, Archive,
  PenLine, Megaphone, BarChart2, Calendar,
  StickyNote, FileEdit, Bell, Bot, History, ChevronDown, ChevronUp, Share2, FileSignature,
} from 'lucide-react';
import Breadcrumb from '../components/Breadcrumb';
import AssetsSection from '../components/cliente/AssetsSection';
import BrandChecklistSection from '../components/cliente/BrandChecklistSection';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import ApprovazioniSection from '../components/cliente/ApprovazioniSection';
import ReportSection from '../components/cliente/ReportSection';
import ArchivioSection from '../components/cliente/ArchivioSection';
import api from '../api';

const FUNCTIONAL_SECTIONS = ['assets', 'approvazioni', 'report'];

const SEZIONI = [
  {
    key:'onboarding', label:'Onboarding', Icon: Users,
    hint:'Brief iniziale, obiettivi, deliverable concordati, accessi ricevuti, aspettative del cliente…',
    desc:'Tutto quello che il team deve sapere per iniziare.',
  },
  {
    key:'strategia', label:'Strategia', Icon: Compass,
    hint:'Pillar di contenuto, tone of voice, target audience, posizionamento, analisi competitor…',
    desc:'La direzione strategica del progetto.',
  },
  {
    key:'produzione', label:'Produzione', Icon: Settings2,
    hint:'Workflow di produzione, tool usati, responsabilità nel team, tempistiche di consegna…',
    desc:'Come si lavora operativamente su questo cliente.',
  },
  {
    key:'revisioni', label:'Revisioni', Icon: Search,
    hint:'Cicli di revisione, feedback ricorrenti, cosa funziona e cosa no, note sui round passati…',
    desc:'Storico delle revisioni e note sui feedback.',
  },
  {
    key:'approvazioni', label:'Approvazioni', Icon: CheckSquare2,
    hint:null, desc:'Workflow approvazioni contenuti.',
  },
  {
    key:'assets', label:'Assets', Icon: Archive,
    hint:null, desc:'File e risorse del cliente.',
  },
  {
    key:'copy', label:'Copy', Icon: PenLine,
    hint:'Tone of voice specifico, parole da usare/evitare, esempi di copy approvati, hashtag strategici, claim…',
    desc:'Testi, caption e linee guida di comunicazione.',
  },
  {
    key:'ads', label:'Ads', Icon: Megaphone,
    hint:'Target audience, budget mensile, obiettivi campagna, A/B test attivi, KPI principali, note sulle creatività…',
    desc:'Strategia e note sulle campagne pubblicitarie.',
  },
  {
    key:'report', label:'Report', Icon: BarChart2,
    hint:null, desc:'Report e analytics consegnati al cliente.',
  },
];

const STATO_TASK_ICON = {
  da_fare:   { icon: Circle,       color:'var(--text-dim)' },
  in_corso:  { icon: Clock,        color:'var(--yellow)'   },
  completata:{ icon: CheckCircle2, color:'var(--green)'    },
};

function fmtEur(v) {
  if (!v) return null;
  return `€${Number(v).toLocaleString('it-IT', { minimumFractionDigits:0, maximumFractionDigits:0 })}`;
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d+'T00:00:00').toLocaleDateString('it-IT', { day:'numeric', month:'short', year:'numeric' });
}

function relTime(d) {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'adesso';
  if (m < 60) return `${m} min fa`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h fa`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}g fa`;
  return new Date(d).toLocaleDateString('it-IT', { day:'numeric', month:'short' });
}

function parseNoteInterne(text) {
  if (!text) return [];
  return text.split('\n\n').filter(Boolean).map(line => {
    const match = line.match(/^\[(.+?)\]\s([\s\S]+)$/);
    if (!match) return null;
    const [, dateStr, content] = match;
    const parts = dateStr.split(', ');
    if (parts.length < 2) return null;
    const [d, mo, y] = parts[0].split('/');
    const date = new Date(`${y}-${mo}-${d}T${parts[1]}`);
    if (isNaN(date.getTime())) return null;
    return { type:'nota', date, content: content.trim() };
  }).filter(Boolean);
}

function HistoryTimeline({ entries, sezioni }) {
  const [openIds, setOpenIds] = React.useState({});
  const toggle = id => setOpenIds(p => ({ ...p, [id]: !p[id] }));

  // Raggruppa per sezione
  const bySezione = entries.reduce((acc, e) => {
    if (!acc[e.sezione]) acc[e.sezione] = [];
    acc[e.sezione].push(e);
    return acc;
  }, {});

  const sezioniConHistory = sezioni.filter(s => bySezione[s.key]);

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      {sezioniConHistory.map(s => {
        const { Icon } = s;
        const vers = bySezione[s.key];
        return (
          <div key={s.key}>
            {/* Sezione header */}
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
              <Icon size={14} color="var(--text-muted)"/>
              <span style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>{s.label}</span>
              <span style={{ fontSize:10.5, color:'var(--text-dim)' }}>{vers.length} versione{vers.length !== 1 ? 'i' : 'e'}</span>
            </div>
            {/* Versioni */}
            <div style={{ position:'relative', paddingLeft:24 }}>
              <div style={{ position:'absolute', left:7, top:0, bottom:0, width:1, background:'var(--border)' }}/>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {vers.map((v, i) => {
                  const isOpen = !!openIds[v.id];
                  const isFirst = i === 0;
                  return (
                    <div key={v.id} style={{ position:'relative' }}>
                      {/* Dot */}
                      <div style={{ position:'absolute', left:-24, top:10, width:14, height:14, borderRadius:'50%',
                        background: isFirst ? 'var(--cyan)' : 'var(--surface)',
                        border:`2px solid ${isFirst ? 'var(--cyan)' : 'var(--border)'}`,
                        zIndex:1 }}/>
                      {/* Card versione */}
                      <div style={{ border:'1px solid var(--border)', borderRadius:10, overflow:'hidden', background:'var(--surface)' }}>
                        <div onClick={() => toggle(v.id)}
                          style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', cursor:'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.background='var(--surface-h)'}
                          onMouseLeave={e => e.currentTarget.style.background=''}>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:12, fontWeight:600, color: isFirst ? 'var(--cyan)' : 'var(--text)' }}>
                              {isFirst ? 'Versione più recente archiviata' : `Versione del ${new Date(v.modificato_il).toLocaleDateString('it-IT', { day:'numeric', month:'long', year:'numeric' })}`}
                            </div>
                            <div style={{ fontSize:10.5, color:'var(--text-dim)', marginTop:2, display:'flex', gap:8 }}>
                              <span>{new Date(v.modificato_il).toLocaleString('it-IT', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })}</span>
                              {v.autore_nome && <span style={{ color:'var(--green)' }}>· {v.autore_nome}</span>}
                              {v.contenuto && <span>· {v.contenuto.length} caratteri</span>}
                            </div>
                          </div>
                          {isOpen
                            ? <ChevronUp size={13} color="var(--text-dim)" style={{ flexShrink:0 }}/>
                            : <ChevronDown size={13} color="var(--text-dim)" style={{ flexShrink:0 }}/>}
                        </div>
                        {isOpen && (
                          <div style={{ padding:'12px 14px', borderTop:'1px solid var(--border)', background:'var(--bg3)' }}>
                            {v.contenuto ? (
                              <div style={{ fontSize:12.5, color:'var(--text)', lineHeight:1.75, whiteSpace:'pre-wrap', wordBreak:'break-word' }}>
                                {v.contenuto}
                              </div>
                            ) : (
                              <div style={{ fontSize:12, color:'var(--text-dim)' }}>Sezione vuota in questa versione.</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ActivityTimeline({ events, onSezioneClick }) {
  return (
    <div style={{ position:'relative' }}>
      <div style={{ position:'absolute', left:15, top:4, bottom:4, width:1, background:'var(--border)' }}/>
      <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
        {events.map((ev, i) => {
          const { Icon: EvIcon } = ev;
          const isSezione = ev.type === 'sezione';
          return (
            <div key={i}
              onClick={isSezione && onSezioneClick ? () => onSezioneClick(ev.sezione) : undefined}
              style={{ display:'flex', gap:14, paddingBottom:16, cursor: isSezione && onSezioneClick ? 'pointer' : 'default' }}
              onMouseEnter={isSezione && onSezioneClick ? e => e.currentTarget.querySelector('.log-c').style.background='var(--surface-h)' : undefined}
              onMouseLeave={isSezione && onSezioneClick ? e => e.currentTarget.querySelector('.log-c').style.background='transparent' : undefined}>
              <div style={{ position:'relative', zIndex:1, flexShrink:0, width:30, display:'flex', justifyContent:'center', paddingTop:2 }}>
                <div style={{ width:30, height:30, borderRadius:'50%',
                  background: isSezione ? 'var(--cyan-dim)' : 'var(--surface)',
                  border:`1.5px solid ${isSezione ? 'rgba(0,212,255,0.35)' : 'var(--border)'}`,
                  display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <EvIcon size={13} color={isSezione ? 'var(--cyan)' : 'var(--text-muted)'}/>
                </div>
              </div>
              <div className="log-c" style={{ flex:1, minWidth:0, borderRadius:8, padding:'6px 10px', transition:'background 0.12s' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
                  <span style={{ fontSize:12.5, fontWeight:600, color:'var(--text)' }}>
                    {isSezione ? `${ev.label} aggiornata` : ev.content}
                  </span>
                  <span style={{ fontSize:10.5, color:'var(--text-dim)', flexShrink:0 }}>{relTime(ev.date)}</span>
                </div>
                {isSezione && (
                  <div style={{ fontSize:11.5, color:'var(--text-muted)', display:'flex', alignItems:'center', gap:6, marginTop:2 }}>
                    {ev.autore
                      ? <><Bot size={10} color="var(--green)"/><span style={{ color:'var(--green)' }}>{ev.autore}</span></>
                      : <span>Modifica manuale</span>}
                  </div>
                )}
                <div style={{ fontSize:11, color:'var(--text-dim)', marginTop:isSezione ? 2 : 0 }}>
                  {ev.date.toLocaleString('it-IT', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ClienteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { user } = useAuth();
  const puoCondividere = ['admin', 'boss', 'social_manager'].includes(user?.ruolo);

  const condividiPortale = async () => {
    try {
      const r = await api.get(`/portal_admin/${id}`, { silent: true });
      const url = `${window.location.origin}/portal/${r.data.token}`;
      try { await navigator.clipboard.writeText(url); success('Link portale copiato negli appunti'); }
      catch { window.prompt('Copia il link del portale cliente:', url); }
    } catch { error('Impossibile generare il link del portale'); }
  };
  const [cliente, setCliente] = useState(null);
  const [sezioni, setSezioni] = useState([]);
  const [taskList, setTaskList] = useState([]);
  const [pedStats, setPedStats] = useState(null);
  const [sezioneAttiva, setSezioneAttiva] = useState('onboarding');
  const [vista, setVista] = useState('corrente');
  const [editing, setEditing] = useState(false);
  const [contenuto, setContenuto] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAllTasks, setShowAllTasks] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [editCliente, setEditCliente] = useState(false);
  const [clienteForm, setClienteForm] = useState({});
  const [savingCliente, setSavingCliente] = useState(false);
  const [noteRapida, setNoteRapida] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!id) return;
    Promise.all([
      api.get(`/clienti/${id}`),
      api.get(`/cliente_sezioni/${id}`),
      api.get(`/task?cliente_id=${id}`),
      api.get(`/ped/stats/${id}?orizzonte=corrente`),
    ]).then(([c, s, t, p]) => {
      setCliente(c.data);
      setClienteForm({ valore_contratto: c.data.valore_contratto || '', note_interne: c.data.note_interne || '',
        contenuti_quota: c.data.contenuti_quota || '', contenuti_periodo: c.data.contenuti_periodo || 'mese' });
      setSezioni(s.data);
      setTaskList(t.data);
      setPedStats(p.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  const logoInputRef = useRef(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const onLogoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const up = await api.post('/upload', fd, { headers:{ 'Content-Type':'multipart/form-data' } });
      const r = await api.put(`/clienti/${id}`, { logo_url: up.data.url });
      setCliente(r.data);
    } catch { alert('Errore nel caricamento del logo'); }
    setUploadingLogo(false);
    if (e.target) e.target.value = '';
  };

  useEffect(() => {
    const s = sezioni.find(s => s.sezione === sezioneAttiva);
    setContenuto(s?.contenuto || '');
    setEditing(false);
  }, [sezioneAttiva, sezioni]);

  const saveSezione = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/cliente_sezioni/${id}/${sezioneAttiva}`, { contenuto });
      setSezioni(prev => prev.map(s => s.sezione === sezioneAttiva ? { ...s, ...res.data } : s));
      setEditing(false);
      success(`Sezione "${sezioneInfo?.label || sezioneAttiva}" salvata`);
    } catch {}
    setSaving(false);
  };

  // Contratto a contenuti (X a settimana / al mese): campo presente solo con la migrazione applicata
  const haContrattoContenuti = !!cliente && 'contenuti_quota' in cliente;
  const puoContratto = ['admin', 'boss', 'direttore', 'social_manager'].includes(user?.ruolo);
  const puoValore = ['admin', 'boss', 'direttore'].includes(user?.ruolo);
  const [copertura, setCopertura] = useState(null);
  useEffect(() => {
    if (!id || !cliente?.contenuti_quota) { setCopertura(null); return; }
    api.get(`/ped/copertura?cliente_id=${id}`, { silent: true })
      .then(r => setCopertura((r.data || [])[0] || null)).catch(() => setCopertura(null));
  }, [id, cliente?.contenuti_quota, cliente?.contenuti_periodo]);

  const saveClienteExtra = async () => {
    setSavingCliente(true);
    try {
      const body = { ...cliente, ...clienteForm };
      if (haContrattoContenuti) body.contenuti_quota = clienteForm.contenuti_quota === '' ? 0 : Number(clienteForm.contenuti_quota);
      const res = await api.put(`/clienti/${id}`, body);
      setCliente(res.data);
      setEditCliente(false);
      success('Info contratto aggiornate');
    } catch {}
    setSavingCliente(false);
  };

  const saveNoteRapida = async () => {
    if (!noteRapida.trim()) return;
    setSavingNote(true);
    try {
      const now = new Date().toLocaleString('it-IT');
      const nota = `[${now}] ${noteRapida.trim()}`;
      const existenti = clienteForm.note_interne || '';
      const nuove = existenti ? nota + '\n\n' + existenti : nota;
      await api.put(`/clienti/${id}`, { ...cliente, note_interne: nuove });
      setCliente(c => ({ ...c, note_interne: nuove }));
      setClienteForm(f => ({ ...f, note_interne: nuove }));
      setNoteRapida('');
      success('Nota aggiunta');
    } catch {}
    setSavingNote(false);
  };

  const deleteNota = async (rawEntry) => {
    const entries = (cliente.note_interne || '').split('\n\n').filter(Boolean);
    const nuove = entries.filter(e => e !== rawEntry).join('\n\n');
    try {
      await api.put(`/clienti/${id}`, { ...cliente, note_interne: nuove });
      setCliente(c => ({ ...c, note_interne: nuove }));
      setClienteForm(f => ({ ...f, note_interne: nuove }));
    } catch {}
  };

  if (loading) return <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)' }}>Caricamento…</div>;
  if (!cliente) return <div style={{ padding:40, textAlign:'center', color:'var(--text-muted)' }}>Cliente non trovato</div>;

  const colori = ['#a855f7','#00d4ff','#10b981','#f59e0b','#f43f5e','#3b82f6'];
  const color = colori[cliente.nome.charCodeAt(0) % colori.length];
  const initials = cliente.nome.split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2);
  const sezioneData = sezioni.find(s => s.sezione === sezioneAttiva);
  const sezioneInfo = SEZIONI.find(s => s.key === sezioneAttiva);

  const taskStats = {
    da_fare: taskList.filter(t => t.stato === 'da_fare').length,
    in_corso: taskList.filter(t => t.stato === 'in_corso').length,
    completata: taskList.filter(t => t.stato === 'completata').length,
  };
  const tasksVisibili = showAllTasks ? taskList : taskList.slice(0, 5);

  const activityLogAll = [
    ...sezioni.filter(s => s.aggiornato_il).map(s => ({
      type: 'sezione',
      date: new Date(s.aggiornato_il),
      sezione: s.sezione,
      autore: s.aggiornato_da_nome || null,
      label: SEZIONI.find(x => x.key === s.sezione)?.label || s.sezione,
      Icon: SEZIONI.find(x => x.key === s.sezione)?.Icon || FileEdit,
    })),
    ...parseNoteInterne(cliente.note_interne).map(n => ({
      type: 'nota',
      date: n.date,
      content: n.content,
      autore: null,
      Icon: StickyNote,
    })),
    ...(taskList || []).filter(t => t.creato_il || t.data_scadenza).map(t => ({
      type: 'task',
      date: new Date(t.creato_il || (t.data_scadenza + 'T00:00:00')),
      content: t.stato === 'completata' ? `Task completata: ${t.titolo}` : `Task: ${t.titolo}`,
      autore: null,
      Icon: t.stato === 'completata' ? CheckCircle2 : CheckSquare2,
    })),
  ].filter(e => e.date && !isNaN(e.date.getTime())).sort((a, b) => b.date - a.date);

  const activityLog = activityLogAll.slice(0, 20);

  const navigateToSezione = (key) => {
    setSezioneAttiva(key);
    setVista('corrente');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openStorico = () => {
    setVista('archivio');
    if (history.length === 0 && !historyLoading) {
      setHistoryLoading(true);
      api.get(`/cliente_sezioni/${id}/history`)
        .then(r => setHistory(r.data))
        .catch(() => {})
        .finally(() => setHistoryLoading(false));
    }
  };

  // ── Bento blocks (estratti per riordinarli su mobile) ───────────────────────
  const blkSezione = (
    <div className="eden-card" style={{ padding:'16px 20px' }}>
      {FUNCTIONAL_SECTIONS.includes(sezioneAttiva) ? (
        <>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16 }}>
            {sezioneInfo && <sezioneInfo.Icon size={15} color="var(--text-muted)"/>}
            <div>
              <div style={{ fontSize:14, fontWeight:600, color:'var(--text)' }}>{sezioneInfo?.label}</div>
              {sezioneInfo?.desc && <div style={{ fontSize:11, color:'var(--text-dim)', marginTop:1 }}>{sezioneInfo.desc}</div>}
            </div>
          </div>
          {sezioneAttiva === 'assets'       && (
            <>
              <BrandChecklistSection clienteId={id} clienteNome={cliente.azienda || cliente.nome}/>
              <div style={{ height:1, background:'var(--border)', margin:'26px 0 22px' }}/>
              <div style={{ fontSize:13, fontWeight:600, color:'var(--text)', marginBottom:14 }}>File caricati</div>
              <AssetsSection clienteId={id}/>
            </>
          )}
          {sezioneAttiva === 'approvazioni' && <ApprovazioniSection clienteId={id}/>}
          {sezioneAttiva === 'report'       && <ReportSection clienteId={id}/>}
        </>
      ) : (
        <>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
            <div style={{ display:'flex', alignItems:'flex-start', gap:8 }}>
              {sezioneInfo && <sezioneInfo.Icon size={15} color="var(--text-muted)" style={{ marginTop:2 }}/>}
              <div>
                <div style={{ fontSize:14, fontWeight:600, color:'var(--text)' }}>{sezioneInfo?.label}</div>
                {sezioneInfo?.desc && !contenuto && (
                  <div style={{ fontSize:11, color:'var(--text-dim)', marginTop:1 }}>{sezioneInfo.desc}</div>
                )}
              </div>
              {sezioneData?.aggiornato_da_nome && (
                <span style={{ fontSize:10.5, color:'var(--green)', background:'var(--green-dim)', padding:'2px 8px', borderRadius:6, display:'flex', alignItems:'center', gap:4, marginLeft:4 }}>
                  <Bot size={10}/> {sezioneData.aggiornato_da_nome}
                </span>
              )}
            </div>
            {!editing && (
              <button onClick={() => setEditing(true)}
                style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:7, color:'var(--text-muted)', cursor:'pointer', padding:'5px 10px', display:'flex', alignItems:'center', gap:5, fontSize:12, flexShrink:0 }}>
                <Edit2 size={12}/> Modifica
              </button>
            )}
          </div>
          {editing ? (
            <>
              <textarea value={contenuto} onChange={e => setContenuto(e.target.value)}
                placeholder={sezioneInfo?.hint || ''}
                style={{ width:'100%', minHeight:200, background:'var(--bg3)', border:'1px solid rgba(0,212,255,0.3)', borderRadius:8, color:'var(--text)', fontSize:13, padding:'10px 12px', resize:'vertical', outline:'none', fontFamily:'inherit', lineHeight:1.6, boxSizing:'border-box' }}
              />
              <div style={{ display:'flex', gap:8, marginTop:10 }}>
                <button onClick={saveSezione} disabled={saving} className="eden-btn-primary" style={{ fontSize:12 }}>
                  <Save size={13}/> {saving ? 'Salvo…' : 'Salva'}
                </button>
                <button onClick={() => { setEditing(false); setContenuto(sezioneData?.contenuto || ''); }}
                  style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:7, color:'var(--text-muted)', cursor:'pointer', padding:'6px 12px', fontSize:12 }}>
                  Annulla
                </button>
              </div>
            </>
          ) : contenuto ? (
            <TestoFormattato testo={contenuto} />
          ) : (
            <div onClick={() => setEditing(true)} style={{ cursor:'pointer', border:'2px dashed var(--border)', borderRadius:10, padding:'28px 20px', textAlign:'center', transition:'border-color 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor='rgba(0,212,255,0.3)'}
              onMouseLeave={e => e.currentTarget.style.borderColor='var(--border)'}>
              <div style={{ fontSize:12, color:'var(--text-dim)', marginBottom:6 }}>Sezione vuota — clicca per compilare</div>
              {sezioneInfo?.hint && (
                <div style={{ fontSize:11.5, color:'var(--text-dim)', opacity:0.7, lineHeight:1.6 }}>{sezioneInfo.hint}</div>
              )}
            </div>
          )}
          {sezioneData?.aggiornato_il && !editing && (
            <div style={{ fontSize:10.5, color:'var(--text-dim)', marginTop:12 }}>
              Aggiornato {relTime(sezioneData.aggiornato_il)}
            </div>
          )}
        </>
      )}
    </div>
  );

  const blkContratto = (
    <div className="eden-card" style={{ padding:'16px 18px' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
        <span style={{ fontSize:11, fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em' }}>Info contratto</span>
        {(puoValore || (puoContratto && haContrattoContenuti)) && (
          <button onClick={() => setEditCliente(e => !e)}
            style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-dim)', fontSize:11, display:'flex', alignItems:'center', gap:4 }}>
            <Edit2 size={11}/> Modifica
          </button>
        )}
      </div>
      {editCliente ? (
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {puoValore && (
            <div>
              <label style={{ fontSize:11, color:'var(--text-muted)', display:'block', marginBottom:3 }}>Valore mensile (€)</label>
              <input type="number" value={clienteForm.valore_contratto}
                onChange={e => setClienteForm(f => ({ ...f, valore_contratto: e.target.value }))}
                style={{ width:'100%', background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:7, color:'var(--text)', fontSize:13, padding:'7px 10px', outline:'none', boxSizing:'border-box' }}
                placeholder="es. 1500"/>
            </div>
          )}
          {puoContratto && haContrattoContenuti && (
            <div>
              <label style={{ fontSize:11, color:'var(--text-muted)', display:'block', marginBottom:3 }}>Contenuti previsti (storie escluse)</label>
              <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                <input type="number" min="0" max="500" value={clienteForm.contenuti_quota} aria-label="Numero di contenuti"
                  onChange={e => setClienteForm(f => ({ ...f, contenuti_quota: e.target.value }))}
                  style={{ width:76, background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:7, color:'var(--text)', fontSize:13, padding:'7px 10px', outline:'none', boxSizing:'border-box' }}
                  placeholder="es. 12"/>
                <div className="eden-seg" role="group" aria-label="Periodo" style={{ flex:1 }}>
                  {[['settimana', 'a settimana'], ['mese', 'al mese']].map(([k, l]) => (
                    <button key={k} type="button" className={clienteForm.contenuti_periodo === k ? 'is-on' : ''} style={{ flex:1 }}
                      onClick={() => setClienteForm(f => ({ ...f, contenuti_periodo: k }))}>{l}</button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <div style={{ display:'flex', gap:6 }}>
            <button onClick={saveClienteExtra} disabled={savingCliente} className="eden-btn-primary" style={{ fontSize:11, padding:'5px 12px' }}>
              <Save size={11}/> {savingCliente ? 'Salvo…' : 'Salva'}
            </button>
            <button onClick={() => setEditCliente(false)} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:7, color:'var(--text-muted)', cursor:'pointer', padding:'5px 10px', fontSize:11 }}>
              Annulla
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ fontSize:26, fontWeight:700, color:'var(--cyan)', lineHeight:1.1 }}>
            {fmtEur(cliente.valore_contratto) || <span style={{ fontSize:13, color:'var(--text-dim)', fontWeight:400 }}>Non impostato</span>}
            {cliente.valore_contratto && <span style={{ fontSize:12, color:'var(--text-muted)', fontWeight:400 }}>/mese</span>}
          </div>
          {haContrattoContenuti && (
            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', fontSize:12.5, color:'var(--text-muted)', marginTop:10 }}>
              <FileSignature size={13}/>
              {cliente.contenuti_quota ? descriviContratto(cliente.contenuti_quota, cliente.contenuti_periodo) : <span style={{ color:'var(--text-dim)' }}>Contenuti da contratto non impostati</span>}
              {copertura && <BadgeCopertura cop={copertura}/>}
            </div>
          )}
          {cliente.email && <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:8 }}>{cliente.email}</div>}
          {cliente.telefono && <div style={{ fontSize:12, color:'var(--text-muted)' }}>{cliente.telefono}</div>}
        </div>
      )}
    </div>
  );

  const blkPED = (
    <div style={{ background:'var(--cyan-dim)', border:'1px solid rgba(0,212,255,0.2)', borderRadius:12, padding:'16px 18px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
        <Calendar size={15} color="var(--cyan)"/>
        <span style={{ fontWeight:600, fontSize:13, color:'var(--cyan)' }}>Piano editoriale</span>
      </div>
      {pedStats && (
        <div style={{ fontSize:11.5, color:'var(--cyan)', marginBottom:10, opacity:0.8 }}>
          {pedStats.totale} righe · {pedStats.in_review} in review
          {pedStats.prossima_uscita && ` · prossima ${new Date(pedStats.prossima_uscita).toLocaleDateString('it-IT', { day:'numeric', month:'short' })}`}
        </div>
      )}
      <button onClick={() => navigate(`/ped?cliente_id=${id}`)}
        style={{ fontSize:12, color:'var(--cyan)', border:'1px solid rgba(0,212,255,0.4)', background:'transparent', borderRadius:7, padding:'7px 14px', cursor:'pointer', width:'100%' }}>
        Apri il PED →
      </button>
    </div>
  );

  const blkNote = (
    <div className="eden-card" style={{ padding:'16px 20px' }}>
      <div style={{ fontSize:13, fontWeight:600, color:'var(--text)', marginBottom:12, display:'flex', alignItems:'center', gap:7 }}>
        <StickyNote size={14} color="var(--text-muted)"/> Note interne
      </div>
      <div style={{ display:'flex', gap:8, marginBottom:12 }}>
        <input
          value={noteRapida}
          onChange={e => setNoteRapida(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && saveNoteRapida()}
          placeholder="Aggiungi una nota rapida…"
          style={{ flex:1, minWidth:0, background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text)', fontSize:13, padding:'8px 12px', outline:'none', fontFamily:'inherit', boxSizing:'border-box' }}
        />
        <button onClick={saveNoteRapida} disabled={savingNote || !noteRapida.trim()}
          style={{ background:'var(--cyan-dim)', border:'1px solid rgba(0,212,255,0.3)', borderRadius:8, color:'var(--cyan)', cursor:'pointer', padding:'8px 12px', display:'flex', alignItems:'center', fontSize:12, opacity: !noteRapida.trim() ? 0.4 : 1, transition:'opacity 0.15s', flexShrink:0 }}>
          <Plus size={14}/>
        </button>
      </div>
      {cliente.note_interne ? (
        <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:260, overflowY:'auto' }}>
          {(cliente.note_interne).split('\n\n').filter(Boolean).map((raw, i) => {
            const match = raw.match(/^\[(.+?)\]\s([\s\S]+)$/);
            const timestamp = match ? match[1] : null;
            const testo = match ? match[2].trim() : raw.trim();
            return (
              <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:8, background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 10px' }}>
                <div style={{ flex:1, minWidth:0 }}>
                  {timestamp && (
                    <div style={{ fontSize:10.5, color:'var(--text-dim)', marginBottom:3 }}>{timestamp}</div>
                  )}
                  <div style={{ fontSize:12.5, color:'var(--text-muted)', lineHeight:1.6, whiteSpace:'pre-wrap', wordBreak:'break-word' }}>{testo}</div>
                </div>
                <button onClick={() => deleteNota(raw)} title="Rimuovi nota"
                  style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-dim)', padding:'2px', borderRadius:4, display:'flex', flexShrink:0, transition:'color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.color='var(--red)'}
                  onMouseLeave={e => e.currentTarget.style.color='var(--text-dim)'}>
                  <X size={13}/>
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ fontSize:12, color:'var(--text-dim)' }}>Nessuna nota. Scrivi e premi Invio.</div>
      )}
    </div>
  );

  const blkTask = (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:10 }}>
        {[
          { label:'Da fare', val:taskStats.da_fare, color:'var(--text)' },
          { label:'In corso', val:taskStats.in_corso, color:'var(--yellow)' },
          { label:'Fatto',    val:taskStats.completata, color:'var(--green)' },
        ].map(({ label, val, color: c }) => (
          <div key={label} className="eden-card" style={{ padding:'12px 14px' }}>
            <div style={{ fontSize:10.5, color: c, marginBottom:2 }}>{label}</div>
            <div style={{ fontSize:22, fontWeight:700, color: c }}>{val}</div>
          </div>
        ))}
      </div>
      <div className="eden-card" style={{ overflow:'hidden', padding:0 }}>
        {taskList.length === 0 ? (
          <div style={{ padding:16, textAlign:'center', color:'var(--text-dim)', fontSize:12 }}>Nessun task</div>
        ) : tasksVisibili.map((t, i) => {
          const SI = STATO_TASK_ICON[t.stato] || STATO_TASK_ICON.da_fare;
          const Icon = SI.icon;
          return (
            <div key={t.id} onClick={() => navigate(`/task?open=${t.id}`)}
              style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px', borderBottom: i < tasksVisibili.length-1 ? '1px solid var(--border)' : 'none', fontSize:12, cursor:'pointer', transition:'background 0.1s' }}
              onMouseEnter={e => e.currentTarget.style.background='var(--surface-h)'}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}>
              <Icon size={12} color={SI.color} style={{ flexShrink:0 }}/>
              <span style={{ color:'var(--text)', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.titolo}</span>
              {t.data_scadenza && <span style={{ fontSize:10.5, color:'var(--text-dim)', flexShrink:0 }}>{fmtDate(t.data_scadenza)}</span>}
            </div>
          );
        })}
        {taskList.length > 5 && (
          <button onClick={() => setShowAllTasks(v => !v)}
            style={{ width:'100%', padding:'8px', fontSize:11.5, color:'var(--text-muted)', background:'none', border:'none', borderTop:'1px solid var(--border)', cursor:'pointer' }}>
            {showAllTasks ? 'Mostra meno ↑' : `Vedi tutte (${taskList.length}) ↓`}
          </button>
        )}
      </div>
    </div>
  );

  const blkActivity = (
    <div className="eden-card" style={{ gridColumn:'1 / -1', padding:'16px 20px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:18 }}>
        <Bell size={14} color="var(--text-muted)"/>
        <span style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>Log attività</span>
        {activityLog.length > 0 && (
          <span style={{ fontSize:10.5, color:'var(--text-dim)', marginLeft:4 }}>
            {activityLog.length} recenti
            {activityLogAll.length > 20 && (
              <> · <button onClick={() => setVista('archivio')} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--cyan)', fontSize:10.5, padding:0, textDecoration:'underline' }}>vedi tutti ({activityLogAll.length})</button></>
            )}
          </span>
        )}
      </div>
      {activityLog.length === 0 ? (
        <div style={{ fontSize:12, color:'var(--text-dim)', padding:'8px 0' }}>
          Nessuna attività registrata. Le modifiche alle sezioni e le note appariranno qui.
        </div>
      ) : (
        <ActivityTimeline
          events={activityLog}
          onSezioneClick={(key) => { setSezioneAttiva(key); window.scrollTo({ top:0, behavior:'smooth' }); }}
        />
      )}
    </div>
  );

  return (
    <div className="eden-page">
      <Breadcrumb items={[
        { label: 'Clienti attivi', to: '/clienti-attivi' },
        { label: cliente.azienda || cliente.nome || '…' },
      ]} />

      {/* ── Header card ─────────────────────────────────── */}
      <div className="eden-card" style={{ padding:'20px 24px', marginBottom:16, display:'flex', alignItems:'center', gap:18, flexWrap:'wrap' }}>
        <input ref={logoInputRef} type="file" accept="image/*" onChange={onLogoPick} style={{ display:'none' }} />
        <div onClick={() => !uploadingLogo && logoInputRef.current?.click()} title="Carica / cambia logo"
          style={{ position:'relative', width:60, height:60, flexShrink:0, cursor:'pointer' }}>
          <div style={{ width:60, height:60, borderRadius:'50%', overflow:'hidden',
            background: cliente.logo_url ? 'var(--surface)' : `${color}22`,
            border:`2.5px solid ${cliente.logo_url ? 'var(--border)' : color+'66'}`,
            display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, fontWeight:700, color }}>
            {cliente.logo_url
              ? <img src={cliente.logo_url} alt="logo" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
              : initials}
          </div>
          {!cliente.logo_url && (
            <span style={{ position:'absolute', bottom:-3, right:-3, width:20, height:20, borderRadius:'50%',
              background: uploadingLogo ? 'var(--text-dim)' : 'var(--cyan)', color:'var(--on-accent)',
              display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:800,
              border:'2px solid var(--bg)', lineHeight:1 }}>
              {uploadingLogo ? '…' : '+'}
            </span>
          )}
        </div>

        <div style={{ flex:1, minWidth:0 }}>
          <h1 style={{ fontSize:22, fontWeight:700, margin:'0 0 4px', color:'var(--text)', lineHeight:1.2 }}>
            {cliente.azienda || cliente.nome}
          </h1>
          <div style={{ fontSize:12.5, color:'var(--text-muted)', display:'flex', alignItems:'center', flexWrap:'wrap', gap:10 }}>
            {cliente.stato === 'attivo' && <span style={{ color:'var(--green)', fontWeight:500 }}>Attivo</span>}
            {cliente.azienda && (cliente.nome || cliente.cognome) && (
              <span>{cliente.nome} {cliente.cognome || ''}</span>
            )}
            {cliente.settore && <span style={{ color:'var(--text-dim)' }}>{cliente.settore}</span>}
          </div>
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:12, flexShrink:0 }}>
          {fmtEur(cliente.valore_contratto) && (
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:11, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em' }}>Contratto</div>
              <div style={{ fontSize:22, fontWeight:700, color:'var(--cyan)' }}>{fmtEur(cliente.valore_contratto)}<span style={{ fontSize:12, fontWeight:400, color:'var(--text-muted)' }}>/mese</span></div>
            </div>
          )}
          {puoCondividere && (
            <button onClick={condividiPortale} title="Copia il link del portale per il cliente"
              style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:12, fontWeight:600, padding:'7px 13px', borderRadius:9, border:'1px solid rgba(0,212,255,0.3)', background:'var(--cyan-dim)', color:'var(--cyan)', cursor:'pointer', touchAction:'manipulation' }}>
              <Share2 size={13}/> Portale cliente
            </button>
          )}
          {/* Corrente / Archivio toggle */}
          <div style={{ display:'inline-flex', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10, padding:3, gap:3 }}>
            <button onClick={() => setVista('corrente')}
              style={{ fontSize:12, padding:'6px 14px', borderRadius:7, border:'none', cursor:'pointer',
                fontWeight: vista==='corrente' ? 600 : 400,
                background: vista==='corrente' ? 'var(--cyan-dim)' : 'transparent',
                color: vista==='corrente' ? 'var(--cyan)' : 'var(--text-muted)',
                transition:'all 0.15s' }}>
              Corrente
            </button>
            <button onClick={openStorico}
              style={{ fontSize:12, padding:'6px 14px', borderRadius:7, border:'none', cursor:'pointer',
                fontWeight: vista==='archivio' ? 600 : 400,
                background: vista==='archivio' ? 'var(--surface-h)' : 'transparent',
                color: vista==='archivio' ? 'var(--text)' : 'var(--text-muted)',
                transition:'all 0.15s', display:'flex', alignItems:'center', gap:5 }}>
              <History size={11}/> Storico
            </button>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
          MODALITÀ STORICO
      ══════════════════════════════════════════════════════ */}
      {vista === 'archivio' && (
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>

          {/* Banner */}
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:'12px 18px',
            display:'flex', alignItems:'center', gap:10, color:'var(--text-muted)', fontSize:12.5 }}>
            <History size={14} color="var(--text-dim)"/>
            <span>
              <strong style={{ color:'var(--text)' }}>Storico versioni</strong> — ogni salvataggio di una sezione viene archiviato qui.
              {' '}<button onClick={() => setVista('corrente')} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--cyan)', fontSize:12.5, padding:0, textDecoration:'underline' }}>← Torna a Corrente</button>
            </span>
          </div>

          {/* Timeline versioni */}
          <div className="eden-card" style={{ padding:'18px 22px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:20 }}>
              <History size={14} color="var(--text-muted)"/>
              <span style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>Versioni salvate</span>
              {!historyLoading && (
                <span style={{ fontSize:10.5, color:'var(--text-dim)' }}>{history.length} versioni</span>
              )}
            </div>

            {historyLoading ? (
              <div style={{ textAlign:'center', padding:'32px 0', color:'var(--text-dim)', fontSize:13 }}>Caricamento…</div>
            ) : history.length === 0 ? (
              <div style={{ textAlign:'center', padding:'40px 0', color:'var(--text-dim)', fontSize:13 }}>
                <History size={32} style={{ opacity:0.2, marginBottom:12 }}/>
                <div>Nessuna versione salvata ancora.</div>
                <div style={{ fontSize:11.5, marginTop:6 }}>Le versioni precedenti appariranno qui ogni volta che salvi una sezione.</div>
              </div>
            ) : (
              <HistoryTimeline entries={history} sezioni={SEZIONI}/>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          MODALITÀ CORRENTE
      ══════════════════════════════════════════════════════ */}
      {vista === 'corrente' && (
        <>
          {/* ── Checklist di avvio (clienti creati con l'onboarding guidato) ── */}
          <OnboardingChecklist clienteId={id} />

          {/* ── Sezioni tabs ──────────────────────────────── */}
          <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:16 }}>
            {SEZIONI.map(s => {
              const { Icon: TabIcon } = s;
              const hasContent = FUNCTIONAL_SECTIONS.includes(s.key)
                ? false
                : !!sezioni.find(x => x.sezione === s.key)?.contenuto;
              return (
                <button key={s.key} onClick={() => setSezioneAttiva(s.key)}
                  style={{
                    display:'flex', alignItems:'center', gap:5, fontSize:12, padding:'6px 13px',
                    borderRadius:8, border:'1px solid', cursor:'pointer', transition:'all 0.15s',
                    fontWeight: sezioneAttiva===s.key ? 600 : 400,
                    borderColor: sezioneAttiva===s.key ? 'rgba(0,212,255,0.4)' : hasContent ? 'rgba(16,185,129,0.25)' : 'var(--border)',
                    background: sezioneAttiva===s.key ? 'var(--cyan-dim)' : hasContent ? 'rgba(16,185,129,0.05)' : 'var(--surface)',
                    color: sezioneAttiva===s.key ? 'var(--cyan)' : hasContent ? 'var(--green)' : 'var(--text-muted)',
                  }}>
                  <TabIcon size={12}/> {s.label}
                </button>
              );
            })}
          </div>

          {/* ── Bento grid (desktop) / stack riordinato (mobile) ── */}
          {isMobile ? (
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              {blkSezione}
              {blkPED}
              {blkTask}
              {blkNote}
              {blkContratto}
              {blkActivity}
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'minmax(0,2fr) minmax(0,1fr)', gap:14 }}>
              {blkSezione}
              {/* Right sidebar: contratto + PED */}
              <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                {blkContratto}
                {blkPED}
              </div>
              {blkNote}
              {blkTask}
              {blkActivity}
            </div>
          )}
        </>
      )}
    </div>
  );
}
